import { useRef, useState } from "react";
import { useAuth } from "src/context/AuthProvider";
import { useTranslation } from "react-i18next";
import { SettingRow, TextInput, Section } from "./SettingsControls";
import PhoneInput from "src/components/PhoneInput/PhoneInput";
import { SpecialtyPicker } from "./SpecialtyPicker";
import Popup from "src/components/Popup/Popup";
import { normalizePlz } from "src/helpers/sanitizeInput";

type DoctorFields = {
    salutation: string;
    title: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    avatar: string;
    specialty: string[];
    invoiceSenderName: string;
    street: string;
    plz: string;
    city: string;
};

interface DoctorSettingsProps {
    doctor: DoctorFields;
    saved: boolean;
    dirty?: boolean;
    onChange: (d: DoctorFields) => void;
    onSave: () => void;
    onDeleteAccount: () => Promise<void>;
}

const SALUTATION_OPTIONS = ["Herr", "Frau", "Divers"];

const MAX_IMG_BYTES = 5 * 1024 * 1024;

export function DoctorSettings({ doctor, saved, dirty, onChange, onSave, onDeleteAccount }: DoctorSettingsProps) {
    const { user } = useAuth();
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [avatarError, setAvatarError] = useState<string | null>(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > MAX_IMG_BYTES) {
            setAvatarError(t("form.imageTooLarge"));
            return;
        }
        setAvatarError(null);
        const reader = new FileReader();
        reader.onload = () => onChange({ ...doctor, avatar: reader.result as string });
        reader.readAsDataURL(file);
    };

    return (
        <>
        <Section title={t("settings.myProfileTitle")}>
            <SettingRow title={t("form.photo")}>
                <div className="avatar-upload">
                    <div className="avatar-upload__preview" onClick={() => fileInputRef.current?.click()}>
                        {doctor.avatar
                            ? <img src={doctor.avatar} alt="avatar" />
                            : <span>{user?.doctor?.firstName?.charAt(0)}{user?.doctor?.lastName?.charAt(0)}</span>
                        }
                        <div className="avatar-upload__overlay">{t("form.change")}</div>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleAvatarChange}
                    />
                    {avatarError && <p className="avatar-upload__error">{avatarError}</p>}
                    {doctor.avatar && (
                        <button className="avatar-upload__remove" onClick={() => onChange({ ...doctor, avatar: "" })}>
                            {t("form.remove")}
                        </button>
                    )}
                </div>
            </SettingRow>
            <SettingRow title={t("form.salutation")}>
                <select
                    className="settings-select"
                    value={doctor.salutation}
                    onChange={(e) => onChange({ ...doctor, salutation: e.target.value })}
                >
                    <option value="">—</option>
                    {SALUTATION_OPTIONS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                    ))}
                </select>
            </SettingRow>
            <SettingRow title={t("form.title")}>
                <TextInput
                    label={t("form.title")}
                    value={doctor.title}
                    onChange={(v) => onChange({ ...doctor, title: v })}
                    placeholder={t("form.drPlaceholder")}
                />
            </SettingRow>
            <SettingRow title={t("form.firstName")}>
                <TextInput label={t("form.firstName")} value={doctor.firstName} onChange={(v) => onChange({ ...doctor, firstName: v })} />
            </SettingRow>
            <SettingRow title={t("form.lastName")}>
                <TextInput label={t("form.lastName")} value={doctor.lastName} onChange={(v) => onChange({ ...doctor, lastName: v })} />
            </SettingRow>
            <SettingRow title={t("settings.deviatingAddressTitle")} description={t("settings.deviatingAddressDesc")} className="setting-row--top">
                <TextInput
                    label={t("settings.senderOrgName")}
                    value={doctor.invoiceSenderName}
                    onChange={(v) => onChange({ ...doctor, invoiceSenderName: v })}
                />
            </SettingRow>
            <SettingRow title={t("form.streetAndNumber")}>
                <TextInput
                    label={t("form.streetAndNumber")}
                    value={doctor.street}
                    placeholder="Hauptstraße 22a"
                    onChange={(v) => onChange({ ...doctor, street: v })}
                />
            </SettingRow>
            <SettingRow title={t("form.plz")}>
                <TextInput label={t("form.plz")} value={doctor.plz} onChange={(v) => onChange({ ...doctor, plz: normalizePlz(v) })} />
            </SettingRow>
            <SettingRow title={t("form.city")}>
                <TextInput label={t("form.city")} value={doctor.city} onChange={(v) => onChange({ ...doctor, city: v })} />
            </SettingRow>
            <SettingRow title={t("form.email")} description={t("settings.emailAccountDesc")}>
                <TextInput label={t("form.email")} value={doctor.email} onChange={(v) => onChange({ ...doctor, email: v })} />
            </SettingRow>
            <SettingRow title={t("form.phone")}>
                <PhoneInput value={doctor.phone ?? ""} onChange={(v) => onChange({ ...doctor, phone: v })} />
            </SettingRow>
            <SettingRow title={t("settings.specialty")} className="setting-row--top">
                <SpecialtyPicker
                    value={doctor.specialty}
                    onChange={(v) => onChange({ ...doctor, specialty: v })}
                />
            </SettingRow>
        </Section>

        <Section title={t("settings.deleteAccount")}>
            <SettingRow title={t("settings.deleteAccount")} description={t("settings.deleteAccountDesc")}>
                {deleteError && <p className="settings-delete-error">{deleteError}</p>}
                <button
                    className="tbl-btn tbl-btn--danger"
                    onClick={() => { setDeleteError(null); setShowDeleteConfirm(true); }}
                >
                    {t("settings.deleteAccount")}
                </button>
            </SettingRow>
        </Section>

        {showDeleteConfirm && (
            <Popup
                title={t("settings.deleteAccountConfirmTitle")}
                message={t("settings.deleteAccountConfirmMsg")}
                onClose={() => setShowDeleteConfirm(false)}
                actions={[
                    { label: t("form.cancel"), variant: "default", onClick: () => setShowDeleteConfirm(false) },
                    {
                        label: deleting ? "..." : t("settings.deleteAccount"),
                        variant: "danger",
                        disabled: deleting,
                        onClick: async () => {
                            setDeleting(true);
                            try {
                                await onDeleteAccount();
                            } catch (e: any) {
                                setDeleting(false);
                                setShowDeleteConfirm(false);
                                setDeleteError(
                                    e?.message === "LAST_ADMIN"
                                        ? t("settings.deleteAccountLastAdmin")
                                        : e?.message ?? t("form.error"),
                                );
                            }
                        },
                    },
                ]}
            />
        )}
    </>
    );
}
